// utils/shiftFilter.ts

import { ShiftDetails } from "@dev-zeromoblt/o-platform-client";

const extractClassNumber = (gradeValue: string): number | null => {
  const match = gradeValue.match(/CLASS_(\d+)/);
  return match ? parseInt(match[1], 10) : null;
};

const isDropperGrade = (gradeValue: string): boolean => {
  return gradeValue === 'NEET_DROPPER' || gradeValue === 'JEE_DROPPER';
};

const isDropperShift = (shiftName: string): boolean => {
  const upper = shiftName.toUpperCase();
  return upper.includes('JEE') || upper.includes('NEET') || upper.includes('DROPPER');
};

const extractShiftClassRange = (shiftName: string): { min: number; max: number } | null => {
  // Match range like "11th-12th", "07th-10th", "08th-12th"
  // Look for pattern: digits + ordinal suffix + dash + digits + ordinal suffix
  const rangeMatch = shiftName.match(/(\d{1,2})(?:st|nd|rd|th)\s*-\s*(\d{1,2})(?:st|nd|rd|th)/i);
  if (rangeMatch) {
    return {
      min: parseInt(rangeMatch[1], 10),
      max: parseInt(rangeMatch[2], 10),
    };
  }

  // Match single class like "11th", "12th"
  const singleMatch = shiftName.match(/(\d{1,2})(?:st|nd|rd|th)/i);
  if (singleMatch) {
    const cls = parseInt(singleMatch[1], 10);
    return { min: cls, max: cls };
  }

  return null;
};

export const filterShiftsByGrade = (
  shifts: ShiftDetails[],
  gradeValue: string
): ShiftDetails[] => {
  // No grade selected — return empty array
  if (!gradeValue) return [];

  const isDropper = isDropperGrade(gradeValue);
  const classNumber = extractClassNumber(gradeValue);

  return shifts.filter((shift) => {
    const shiftName = shift.name ?? '';
    const shiftIsDropper = isDropperShift(shiftName);

    // If selected grade is JEE/NEET dropper → only show dropper shifts
    if (isDropper) return shiftIsDropper;

    // If selected grade is a class number → exclude dropper shifts
    if (shiftIsDropper) return false;

    const range = extractShiftClassRange(shiftName);

    // If we can't parse a class from shift name, exclude it (strict filtering)
    if (!range) return false;

    // Include only if selected class falls within the shift's range
    return classNumber !== null && classNumber >= range.min && classNumber <= range.max;
  });
};